import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowUpRight } from "lucide-react";
import { PageHeader } from "./fordon";
import { HubEditorial, faqSchema } from "@/components/HubEditorial";
import { guiderFaq, guiderSections } from "@/data/hub-content";

const URL = "https://www.akhusbilfritid.se/guider";

export const Route = createFileRoute("/guider/")({
  // FAQ-schemat byggs i loadern så guidens textdata hamnar i sidans egen
  // chunk i stället för i initial-bundlen.
  codeSplitGroupings: [["loader", "component"]],
  loader: () => ({ faqLd: faqSchema(guiderFaq) }),
  head: ({ loaderData }) => ({
    meta: [
      { title: "Guider för husbil & husvagn – AK Husbil & Fritid" },
      {
        name: "description",
        content:
          "Praktiska guider för husbil & husvagn: körkort, vinterförvaring, köp-checklista och mer. Från AK Husbil & Fritid i Kalix.",
      },
      {
        name: "keywords",
        content:
          "husbil guider, husvagn guide, vilket körkort krävs husbil, husbil vinterförvaring, köpa husbil checklista, reservdelar husbil",
      },
      { property: "og:title", content: "Guider för husbil & husvagn" },
      { property: "og:description", content: "Ärliga, praktiska guider från en handlare med 20 års erfarenhet." },
      { property: "og:url", content: URL },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "@id": `${URL}#collection`,
          name: "Guider för husbil & husvagn",
          url: URL,
          inLanguage: "sv-SE",
          isPartOf: { "@id": "#organization" },
        }),
      },
      ...(loaderData ? [{ type: "application/ld+json", children: JSON.stringify(loaderData.faqLd) }] : []),
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Hem", item: "https://www.akhusbilfritid.se/" },
            { "@type": "ListItem", position: 2, name: "Guider", item: URL },
          ],
        }),
      },
    ],
  }),
  component: GuiderIndex,
});

const guides = [
  {
    slug: "vilket-korkort-kravs-for-husbil",
    title: "Vilket körkort krävs för husbil?",
    excerpt:
      "B, B96, BE, C1 eller C — vi går igenom vad varje körkort tillåter och vilken totalvikt du får köra.",
    tag: "Körkort",
  },
  {
    slug: "husbil-vinterforvaring",
    title: "Vinterförvaring av husbil – komplett checklista",
    excerpt:
      "Från vattensystem och batteri till däck och tätningar. Så förbereder du husbilen inför vintern.",
    tag: "Skötsel",
  },
  {
    slug: "vardera-husbil-husvagn",
    title: "Värdera husbil & husvagn – så räknar du ut rätt pris",
    excerpt:
      "Marknadsprisjämförelse, avdrag för fukt, däck och slitage, plus räkneexempel. Samma metod vi själva använder vid inbyte.",
    tag: "Värdering",
  },
  {
    slug: "kopa-husbil-checklista",
    title: "Köpa husbil – checklista före affären",
    excerpt:
      "20 punkter att kontrollera innan du skriver på: fukt, chassi, gasol, batteri, historik och mer.",
    tag: "Köpguide",
  },
];

function GuiderIndex() {
  return (
    <>
      <PageHeader
        label="Guider · Kunskap som håller"
        title={<>Guider för <span className="font-italic-display text-accent">husbil</span> & husvagn.</>}
        sub="Fokuserade guider för de vanligaste frågorna vi får — körkort, förvaring och kloka val innan du köper."
      />
      <section className="container-edge py-20 md:py-24">
        <ul className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {guides.map((g) => (
            <li key={g.slug}>
              <Link
                to="/guider/$slug"
                params={{ slug: g.slug }}
                className="group flex h-full flex-col rounded-3xl border border-border bg-card p-8 hover-lift"
              >
                <p className="eyebrow">{g.tag}</p>
                <h2 className="mt-4 font-display text-3xl leading-tight">{g.title}</h2>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-muted-foreground">{g.excerpt}</p>
                <span className="mt-6 inline-flex items-center gap-1.5 text-sm font-medium text-accent">
                  Läs guiden <ArrowUpRight size={14} className="transition group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </section>

      <section className="container-edge pb-8">
        <p className="eyebrow">Nationella djupguider</p>
        <div className="mt-5 grid gap-4 md:grid-cols-3">
          {[
            {
              to: "/stallplatser-husbil" as const,
              h: "Ställplatser i Sverige",
              p: "Priser, reglerna för dygnsvila och vildcamping, vinteröppna platser och hur länge du klarar dig utan el.",
            },
            {
              to: "/solceller-husbil" as const,
              h: "Solceller & litiumbatteri",
              p: "Verkligt elbehov i wattimmar, faktiskt solutbyte månad för månad, AGM mot litium och kompletta priser monterat.",
            },
            {
              to: "/fukttest-husbil" as const,
              h: "Fukttest steg för steg",
              p: "Gränsvärden, våra tolv mätpunkter, vad varje skadestadium kostar och hur du gör egenkontrollen i höst.",
            },
            {
              to: "/b96-korkort" as const,
              h: "B96-körkort",
              p: "Exakta viktgränser mot B och BE, kostnad och prov 2026, och varför B96 inte hjälper dig köra tyngre husbil.",
            },
            {
              to: "/husbilsforsakring" as const,
              h: "Husbilsförsäkring",
              p: "Premienivåer, självrisker och de fyra villkorsrader som avgör om skadan faktiskt ersätts.",
            },
            {
              to: "/leasa-husbil" as const,
              h: "Leasa husbil",
              p: "Varför privatleasing knappt finns, vad företagsleasing kostar netto efter moms och när avbetalning vinner.",
            },
            {
              to: "/besiktning-husbil" as const,
              h: "Besiktning av husbil",
              p: "Intervall och priser, de sex vanligaste underkännandena och en egenkontroll på 20 minuter.",
            },
            {
              to: "/dieselvarmare-husbil" as const,
              h: "Dieselvärmare i husbil",
              p: "Uppmätt förbrukning vid −5, −15 och −25 °C, elbehovet, priser 2026 och de fem vanligaste felen.",
            },
            {
              to: "/husbilsdack" as const,
              h: "Husbilsdäck",
              p: "CP-däck, tryck per axel efter vägning, DOT-koden som avgör bytet och vinterdäckskraven.",
            },
            {
              to: "/salja-husbil" as const,
              h: "Sälja husbil",
              p: "Förmedling, inbyte eller privat — vad varje väg ger i handen, och vad du gör när bilen inte säljs.",
            },
            {
              to: "/tvatta-husvagn" as const,
              h: "Tvätta husvagn rätt",
              p: "Metoden inför varje leverans: kemi, svartstrimmor, högtryck, polering och vintertvätt.",
            },
          ].map((c) => (
            <Link
              key={c.to}
              to={c.to}
              className="group flex h-full flex-col rounded-3xl border border-border bg-card p-7 hover-lift"
            >
              <h2 className="font-display text-2xl leading-tight">{c.h}</h2>
              <p className="mt-3 flex-1 text-sm leading-relaxed text-muted-foreground">{c.p}</p>
              <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-accent">
                Läs guiden <ArrowUpRight size={14} className="transition group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </span>
            </Link>
          ))}
        </div>
      </section>

      <section className="container-edge pb-8">
        <p className="eyebrow">Ekonomi · samlad vägledning</p>
        <h2 className="mt-3 max-w-2xl font-display text-4xl leading-tight">
          Jämför finansiering, värdeminskning och försäkring.
        </h2>
        <div className="mt-6 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {[
            {
              to: "/leasa-husbil" as const,
              h: "Leasing & finansiering",
              p: "Privat eller företag, moms, restvärde och avbetalning sida vid sida.",
            },
            {
              to: "/verktyg/manadskostnad-husbil" as const,
              h: "Månadskostnad",
              p: "Räkna på lån, försäkring, skatt, service och bränsle i en summa.",
            },
            {
              to: "/verktyg/avskrivning-husbil" as const,
              h: "Värdeminskning",
              p: "Se hur årsmodell, inköpspris och innehavstid påverkar värdet.",
            },
            {
              to: "/husbilsforsakring" as const,
              h: "Försäkring",
              p: "Premie, självrisk och villkor som påverkar den verkliga kostnaden.",
            },
          ].map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className="group flex h-full flex-col rounded-3xl border border-border bg-card p-7 hover-lift"
            >
              <h3 className="font-display text-2xl leading-tight">{item.h}</h3>
              <p className="mt-3 flex-1 text-sm leading-relaxed text-muted-foreground">{item.p}</p>
              <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-accent">
                Öppna <ArrowUpRight size={14} className="transition group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </span>
            </Link>
          ))}
        </div>
      </section>

      <section className="container-edge pb-8">
        <Link
          to="/verktyg/fuktprotokoll"
          className="group flex flex-col gap-2 rounded-3xl border border-accent/40 bg-accent/5 p-8 hover-lift"
        >
          <p className="eyebrow">Verktyg · Fukt</p>
          <h2 className="font-display text-3xl leading-tight">Skapa ett fuktprotokoll med tolv mätpunkter</h2>
          <p className="max-w-2xl text-sm leading-relaxed text-muted-foreground">
            Fyll i mätvärden, få färgkodad riskbedömning och skriv ut protokollet inför köp eller årlig kontroll.
          </p>
          <span className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-accent">
            Öppna fuktprotokollet <ArrowUpRight size={14} className="transition group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
          </span>
        </Link>
      </section>

      <section className="container-edge py-8">
        <Link
          to="/tester"
          className="group flex flex-col gap-2 rounded-3xl border border-accent/40 bg-accent/5 p-8 hover-lift"
        >
          <p className="eyebrow">Nytt · Egna mätvärden</p>
          <h2 className="font-display text-3xl leading-tight">Våra långtidstester av husbilar</h2>
          <p className="max-w-2xl text-sm leading-relaxed text-muted-foreground">
            Uppmätt bränsleförbrukning, gasolekonomi vid −20 °C, fuktvärden efter tre vintrar och intervjuer med ägare i
            Norrbotten. Siffror från vår egen verkstad, inte från broschyrer.
          </p>
          <span className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-accent">
            Till testerna <ArrowUpRight size={14} className="transition group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
          </span>
        </Link>
      </section>


      <HubEditorial
        eyebrow="Så använder du guiderna"
        sections={guiderSections}
        faq={guiderFaq}
        faqTitle="Vanliga frågor"
      />
    </>
  );
}
